import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { useParams, useNavigate } from 'react-router-dom'
import { authenticatedFetch } from '@services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { useFetch } from '@hooks'
import useAuthz from '@hooks/useAuthz'
import {
  Box,
  Typography,
  Paper,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Fab,
  Tooltip,
  TextField
} from '@mui/material'
import { Add, Remove, Refresh, InsertDriveFile } from '@mui/icons-material'
import PropTypes from 'prop-types'
import { PageLayout, PageHeader } from '@components'
import { LoadingSpinner } from '@components/atoms'
import { getUserOrganization } from '../../../utils/sopUtils.js'
import styles from './CallSOP.module.scss'

const CallSOP = ({ user, isAuthenticated, isLoading }) => {
  const { isAdmin } = useAuthz()
  const { campaignName: campaignFromParams } = useParams()
  const navigate = useNavigate()

  const [selectedCampaign, setSelectedCampaign] = useState(
    campaignFromParams ? decodeURIComponent(campaignFromParams) : ''
  )
  const [sopUrl, setSopUrl] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [imageLoadError, setImageLoadError] = useState(false)
  const [actualDimensions, setActualDimensions] = useState({ width: 0, height: 0 })
  const [zoomLevel, setZoomLevel] = useState(1)
  const [zoomInput, setZoomInput] = useState('100')
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const { data: campaigns, loading: campaignsLoading } = useFetch(apiEndpoints.campaigns, {
    initialData: [],
    skip: !isAdmin,
    transform: (data) => data || [],
    errorMessage: 'Failed to fetch campaign list'
  })

  const MIN_ZOOM = 0.25
  const MAX_ZOOM = 5
  const ZOOM_STEP = 0.25
  const PAN_STEP = 50

  const zoomIn = () => {
    const newZoom = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM)
    setZoomLevel(newZoom)
    setZoomInput(Math.round(newZoom * 100).toString())
  }

  const zoomOut = () => {
    const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM)
    setZoomLevel(newZoom)
    setZoomInput(Math.round(newZoom * 100).toString())
  }

  const resetZoom = () => {
    setZoomLevel(1)
    setZoomInput('100')
    setPanOffset({ x: 0, y: 0 })
  }

  const handleZoomInputChange = (e) => {
    setZoomInput(e.target.value)
  }

  const handleZoomInputSubmit = () => {
    const numValue = parseFloat(zoomInput)
    if (!isNaN(numValue) && numValue >= MIN_ZOOM * 100 && numValue <= MAX_ZOOM * 100) {
      const newZoom = numValue / 100
      setZoomLevel(newZoom)
      setZoomInput(Math.round(newZoom * 100).toString())
    } else {
      setZoomInput(Math.round(zoomLevel * 100).toString())
    }
  }

  const handleZoomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleZoomInputSubmit()
    }
  }

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        setPanOffset((prev) => ({ ...prev, y: prev.y + PAN_STEP }))
        break
      case 'ArrowDown':
        e.preventDefault()
        setPanOffset((prev) => ({ ...prev, y: prev.y - PAN_STEP }))
        break
      case 'ArrowLeft':
        e.preventDefault()
        setPanOffset((prev) => ({ ...prev, x: prev.x + PAN_STEP }))
        break
      case 'ArrowRight':
        e.preventDefault()
        setPanOffset((prev) => ({ ...prev, x: prev.x - PAN_STEP }))
        break
      case '+':
      case '=':
        e.preventDefault()
        zoomIn()
        break
      case '-':
        e.preventDefault()
        zoomOut()
        break
      case '0':
        e.preventDefault()
        resetZoom()
        break
      default:
        break
    }
  }

  const handleMouseDown = (e) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleDoubleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (zoomLevel === 1) {
      const newZoom = 2
      setZoomLevel(newZoom)
      setZoomInput(Math.round(newZoom * 100).toString())
    } else {
      setZoomLevel(1)
      setZoomInput('100')
      setPanOffset({ x: 0, y: 0 })
    }
  }

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPanOffset({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        })
      }
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragStart])

  useEffect(() => {
    const fetchSop = async () => {
      if (!selectedCampaign) return

      setLoading(true)
      setImageLoadError(false)
      try {
        const response = await authenticatedFetch(apiEndpoints.campaignSop(selectedCampaign))
        const data = await response.json()

        if (!response.ok || !data.sop_url) {
          setError(data.error || 'No SOP found for this campaign.')
          setSopUrl('')
        } else {
          setSopUrl(data.sop_url)
          setError('')

          try {
            const svgResponse = await authenticatedFetch(
              apiEndpoints.campaignSopContent(selectedCampaign)
            )
            if (svgResponse.ok) {
              const svgText = await svgResponse.text()

              const tempDiv = document.createElement('div')
              tempDiv.style.position = 'absolute'
              tempDiv.style.visibility = 'hidden'
              tempDiv.style.width = '100%'
              tempDiv.style.height = '100%'
              tempDiv.innerHTML = DOMPurify.sanitize(svgText, { USE_PROFILES: { svg: true } })

              document.body.appendChild(tempDiv)

              const svgElement = tempDiv.querySelector('svg')
              if (svgElement) {
                const bbox = svgElement.getBoundingClientRect()

                const measuredWidth = bbox.width || svgElement.clientWidth || 730
                const measuredHeight = bbox.height || svgElement.clientHeight || 730

                setActualDimensions({
                  width: measuredWidth,
                  height: measuredHeight
                })
              }

              document.body.removeChild(tempDiv)
            }
          } catch (svgError) {
            console.error('Error measuring SVG dimensions:', svgError)
          }
        }
      } catch (err) {
        setError('Network error fetching SOP.')
        setSopUrl('')
      } finally {
        setLoading(false)
      }
    }

    fetchSop()
  }, [selectedCampaign])

  useEffect(() => {
    if (campaignFromParams) {
      const decodedCampaign = decodeURIComponent(campaignFromParams)
      setSelectedCampaign(decodedCampaign)
    }
  }, [campaignFromParams, campaigns])

  useEffect(() => {
    if (isLoading || !isAuthenticated) return

    const userCampaign = getUserOrganization(user)

    if (!isAdmin && userCampaign) {
      const decodedCampaignFromParams = campaignFromParams
        ? decodeURIComponent(campaignFromParams)
        : ''

      if (decodedCampaignFromParams !== userCampaign) {
        navigate(`/sop/${encodeURIComponent(userCampaign)}`, { replace: true })
        return
      }
    }
  }, [campaignFromParams, user, isAuthenticated, isLoading, navigate, isAdmin])

  if (isAdmin && campaignsLoading) {
    return (
      <PageLayout>
        <PageHeader
          title='Call SOP'
          subtitle='View your standard operating procedures and call handling guidelines'
        />
        <LoadingSpinner />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader
        title='Call SOP'
        subtitle='View your standard operating procedures and call handling guidelines'
      />

      <Box mb={3} maxWidth={600}>
        <FormControl fullWidth>
          <InputLabel>Campaign</InputLabel>
          <Select
            value={selectedCampaign}
            onChange={(e) => isAdmin && setSelectedCampaign(e.target.value)}
            readOnly={!isAdmin}
            IconComponent={isAdmin ? undefined : () => null}
            SelectDisplayProps={{ 'data-testid': 'sop-campaign-select' }}
          >
            {isAdmin && (
              <MenuItem value=''>
                <em>-- Select Campaign --</em>
              </MenuItem>
            )}
            {isAdmin
              ? campaigns.map((c) => (
                  <MenuItem key={c.original_name} value={c.original_name}>
                    {c.campaign_name}
                  </MenuItem>
                ))
              : selectedCampaign && (
                  <MenuItem value={selectedCampaign}>{selectedCampaign}</MenuItem>
                )}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className={styles.emptyState}>
          <InsertDriveFile className={styles.emptyStateIcon} aria-hidden='true' />
          <Typography variant='h6' className={styles.emptyStateTitle}>
            No SOP Available
          </Typography>
          <Typography variant='body2' className={styles.emptyStateText}>
            {error === 'No SOP found for this campaign.'
              ? 'There is no Standard Operating Procedure uploaded for this campaign yet. Please contact your administrator to have one added.'
              : error}
          </Typography>
        </div>
      ) : sopUrl ? (
        <Paper elevation={0} className={styles.sopPaper}>
          {/* Zoom Controls - Only show when image is loaded */}
          {!imageLoadError && (
            <>
              <Box className={styles.zoomControls}>
                <Tooltip title='Zoom In' placement='left'>
                  <span>
                    <Fab
                      size='small'
                      color='primary'
                      onClick={zoomIn}
                      disabled={zoomLevel >= MAX_ZOOM}
                    >
                      <Add />
                    </Fab>
                  </span>
                </Tooltip>
                <Tooltip title='Zoom Out' placement='left'>
                  <span>
                    <Fab
                      size='small'
                      color='primary'
                      onClick={zoomOut}
                      disabled={zoomLevel <= MIN_ZOOM}
                    >
                      <Remove />
                    </Fab>
                  </span>
                </Tooltip>
                <Tooltip title='Reset Zoom' placement='left'>
                  <span>
                    <Fab
                      size='small'
                      color='secondary'
                      onClick={resetZoom}
                      disabled={zoomLevel === 1 && panOffset.x === 0 && panOffset.y === 0}
                    >
                      <Refresh />
                    </Fab>
                  </span>
                </Tooltip>
              </Box>

              {/* Zoom Level Input */}
              <Box className={styles.zoomInputWrapper}>
                <TextField
                  value={zoomInput}
                  onChange={handleZoomInputChange}
                  onBlur={handleZoomInputSubmit}
                  onKeyDown={handleZoomInputKeyDown}
                  variant='outlined'
                  size='small'
                  className={styles.zoomInput}
                  InputProps={{
                    endAdornment: '%',
                    style: {
                      fontSize: 'var(--font-size-base)',
                      padding: '0.25rem 0.5rem'
                    }
                  }}
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      padding: '0.25rem'
                    }
                  }}
                />
              </Box>
            </>
          )}

          {imageLoadError ? (
            <div
              className={styles.emptyState}
              style={{ border: 'none', minHeight: 'auto', padding: '2rem' }}
            >
              <InsertDriveFile className={styles.emptyStateIcon} aria-hidden='true' />
              <Typography variant='h6' className={styles.emptyStateTitle}>
                Image Load Error
              </Typography>
              <Typography variant='body2' className={styles.emptyStateText}>
                The image could not be displayed. It may be an invalid link or the host is
                unavailable.
              </Typography>
            </div>
          ) : (
            <Box
              className={styles.imageViewport}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
              <Box
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                  transformOrigin: '30% 21%',
                  transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                  userSelect: 'none'
                }}
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
              >
                <img
                  src={sopUrl}
                  alt='Standard Operating Procedure'
                  draggable={false}
                  onError={() => {
                    setImageLoadError(true)
                    setZoomLevel(1)
                    setZoomInput('100')
                    setPanOffset({ x: 0, y: 0 })
                  }}
                  className={styles.sopImage}
                  style={{
                    width: actualDimensions.width > 0 ? `${actualDimensions.width}px` : 'auto',
                    height: actualDimensions.height > 0 ? `${actualDimensions.height}px` : 'auto'
                  }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      ) : null}
    </PageLayout>
  )
}

CallSOP.propTypes = {
  user: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default CallSOP
